import type {
	CourseMindItemSearchResult,
	MindItem,
	MindItemSearchResult,
	ProductSearchResult,
	TeachingFormatListResponse,
	Topic,
	WorkloadListResponse,
} from '@kimuno/api-sdk';
import _ from 'lodash';
import immutable from 'object-path-immutable';

import { LOGOUT, type LogoutAction } from '../actions/auth.actionTypes';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import {
	CREATE_COURSE_REGISTRATION,
	type CreateCourseRegistrationAction,
	UPDATE_COURSE_REGISTRATION,
	type UpdateCourseRegistrationAction,
} from '../actions/course.actionTypes';
import {
	PIN_MIND_ITEM,
	PIN_PRODUCT,
	type PinMindItemAction,
	type PinProductAction,
	UNPIN_DOCUMENT,
	type UnpinDocumentAction,
} from '../actions/pinned.actionTypes';
import {
	FETCH_TEACHING_FORMATS,
	FETCH_WORKLOADS,
	type FetchTeachingFormatsAction,
	type FetchWorkloadsAction,
	SEARCH_COURSE_MIND_ITEMS,
	SEARCH_MIND_ITEMS,
	SEARCH_PRODUCTS,
	SEARCH_REQUEST_ABORTED,
	SEARCH_TOPICS,
	SEARCH_UNIFIED,
	SEARCH_VIDEO_MIND_ITEMS,
	SET_RESULTS_SEARCH_ID,
	type SearchCourseMindItemsAction,
	type SearchMindItemsAction,
	type SearchProductsAction,
	type SearchRequestAbortedAction,
	type SearchSlice,
	type SearchStartPayload,
	type SearchTopicsAction,
	type SearchUnifiedAction,
	type SearchVideoMindItemsAction,
	type SetResultsSearchIdAction,
} from '../actions/search.actionTypes';
import { COURSE_REGISTRATION_STATUS, searchSettings } from '../constants';
import type { Pagination, SearchFilters } from '../types/search';
import { arrayToObject } from '../utils/arrayToObject';
import searchResultsStorageManager from '../utils/searchResultsStorageManager';
import type { StoreItem } from '.';

type SearchActionTypes =
	| SearchMindItemsAction
	| SearchVideoMindItemsAction
	| SearchCourseMindItemsAction
	| SearchProductsAction
	| SearchUnifiedAction
	| SearchRequestAbortedAction
	| SearchTopicsAction
	| SetResultsSearchIdAction
	| PinMindItemAction
	| PinProductAction
	| FetchTeachingFormatsAction
	| UnpinDocumentAction
	| FetchWorkloadsAction
	| CreateCourseRegistrationAction
	| UpdateCourseRegistrationAction
	| LogoutAction;

// A LOADING page remembers the request that marked it, so an ABORTED can tell whether the
// page is still its own or already belongs to the request that replaced it.
export type MindItemPage = StoreItem<string[]> & { requestId?: number };
export type ProductPage = StoreItem<string[]> & { requestId?: number };
export type UnifiedPage = StoreItem<string[]> & { requestId?: number };
export type Page = MindItemPage | ProductPage | UnifiedPage;

export interface SearchInitialStateType {
	searchFilters?: SearchFilters;
	courseSearchFilters?: SearchFilters;
	// The search journey the pages currently in the store were fetched under. Split the
	// same way the filters are, because the courses page searches independently of the
	// library tabs. Undefined until a search runs inside a journey.
	resultsSearchId?: string;
	courseResultsSearchId?: string;
	mindItems: {
		pages: MindItemPage[];
		pagination: Pagination;
		byId: {
			[key: string]: MindItemSearchResult;
		};
	};
	videoMindItems: {
		pages: MindItemPage[];
		pagination: Pagination;
		byId: {
			[key: string]: MindItemSearchResult;
		};
	};
	courseMindItems: {
		pages: MindItemPage[];
		pagination: Pagination;
		byId: {
			[key: string]: CourseMindItemSearchResult;
		};
	};
	products: {
		pages: ProductPage[];
		pagination: Pagination;
		byId: {
			[key: string]: ProductSearchResult;
		};
	};
	unified: {
		pages: UnifiedPage[];
		pagination: Pagination;
		byId: {
			[key: string]: MindItemSearchResult | ProductSearchResult;
		};
	};
	subTopics: {
		status: AsyncActionStatus;
		terms: {
			[key: string]: Topic[];
		};
	};
	workloads: {
		status: AsyncActionStatus;
		data: WorkloadListResponse;
	};
	teachingFormats: {
		status: AsyncActionStatus;
		data: TeachingFormatListResponse;
	};
}

const initialPagination: Pagination = {
	total: -1,
	limit: searchSettings.itemsPerPage,
	offset: 0,
};

// A slice reset inside an `immutable(state)` chain must not reuse a shared object: the
// chain treats what it is handed as its own and writes the pages it marks next straight
// into it, which would leave those pages inside the initial state for every later reset.
const createEmptySlice = () => ({
	pages: [],
	pagination: initialPagination,
	byId: {},
});

const initialState: SearchInitialStateType = {
	searchFilters: undefined,
	mindItems: createEmptySlice(),
	videoMindItems: createEmptySlice(),
	courseMindItems: createEmptySlice(),
	products: createEmptySlice(),
	unified: createEmptySlice(),
	subTopics: {
		status: AsyncActionStatus.INITIAL,
		terms: {},
	},
	workloads: {
		status: AsyncActionStatus.INITIAL,
		data: [],
	},
	teachingFormats: {
		status: AsyncActionStatus.INITIAL,
		data: [],
	},
};

const librarySlices: SearchSlice[] = ['mindItems', 'videoMindItems', 'products', 'unified'];

// The library tabs search under one shared `searchFilters`, so a first page requested for
// other filters is a new search for all of them: the other tabs' pages are dropped here,
// which leaves their loading state NONE until they are opened again.
const startLibrarySearch = (
	state: SearchInitialStateType,
	slice: SearchSlice,
	payload: SearchStartPayload
): SearchInitialStateType => {
	const { filters, pagination, requestId, searchId } = payload;
	const incomingPagesStartIndex = Math.floor(pagination.offset / searchSettings.itemsPerPage);
	const incomingPagesEndIndex = Math.floor(
		(pagination.offset + pagination.limit) / searchSettings.itemsPerPage
	);

	let updatedState = immutable(state);

	if (pagination.offset === 0) {
		const slicesToReset = _.isEqual(filters, state.searchFilters) ? [slice] : librarySlices;
		for (const sliceToReset of slicesToReset) {
			updatedState = updatedState.set([sliceToReset], createEmptySlice());
		}
	}

	for (let i = incomingPagesStartIndex; i < incomingPagesEndIndex; i++) {
		updatedState = updatedState.set([slice, 'pages', i], {
			status: AsyncActionStatus.LOADING,
			requestId,
		});
	}

	updatedState = updatedState.set(['searchFilters'], filters);
	updatedState = updatedState.set(['resultsSearchId'], searchId);
	searchResultsStorageManager.setSearchFilters(filters);
	return updatedState.value();
};

const getSubStores = (
	state: SearchInitialStateType,
	itemId: string,
	types: string[]
): SearchSlice[] => {
	const subStores: SearchSlice[] = [];

	types.forEach((type) => {
		if (type === 'product') {
			subStores.push('products');
		} else if (type === 'mindItem' && state.mindItems.byId[itemId] !== undefined) {
			subStores.push('mindItems');
		} else if (type === 'videoMindItem' && state.videoMindItems.byId[itemId] !== undefined) {
			subStores.push('videoMindItems');
		} else if (type === 'courseMindItem' && state.courseMindItems.byId[itemId] !== undefined) {
			subStores.push('courseMindItems');
		} else if (state.unified.byId[itemId] !== undefined) {
			subStores.push('unified');
		}
	});

	return subStores;
};

// biome-ignore lint/style/useDefaultParameterLast: parameter order is a fixed contract (Redux reducer signature / existing call sites)
const search = (state = initialState, action: SearchActionTypes): SearchInitialStateType => {
	switch (action.type) {
		case LOGOUT.SUCCESS:
			return initialState;

		case SET_RESULTS_SEARCH_ID: {
			const key = action.payload.isCourse ? 'courseResultsSearchId' : 'resultsSearchId';
			return immutable.set(state, key, action.payload.searchId);
		}

		case SEARCH_MIND_ITEMS.START:
			return startLibrarySearch(state, 'mindItems', action.payload);

		case SEARCH_MIND_ITEMS.SUCCESS: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPagesStartIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);
			const incomingPagesEndIndex: number = Math.floor(
				(requestPagination.offset + requestPagination.limit) / searchSettings.itemsPerPage
			);
			let updatedState = immutable(state);
			for (let i = incomingPagesStartIndex; i < incomingPagesEndIndex; i++) {
				const firstItemIndex = (i - incomingPagesStartIndex) * searchSettings.itemsPerPage;
				const lastItemIndex = (i - incomingPagesStartIndex + 1) * searchSettings.itemsPerPage;
				const pageData = action.result.data.slice(firstItemIndex, lastItemIndex);
				const newPage: MindItemPage = {
					status: AsyncActionStatus.READY,
					data: pageData.map((mindItem) => mindItem.id),
				};
				updatedState = updatedState.set(['mindItems', 'pages', i], newPage);
				updatedState = updatedState.merge(
					['mindItems', 'byId'],
					arrayToObject(pageData, (mindItem) => mindItem.id)
				);
			}
			return updatedState
				.set(['mindItems', 'pagination', 'total'], action.result.meta.pagination.total)
				.set(
					['mindItems', 'pagination', 'offset'],
					state.mindItems.pagination.offset + requestPagination.limit
				)
				.value();
		}

		case SEARCH_VIDEO_MIND_ITEMS.START:
			return startLibrarySearch(state, 'videoMindItems', action.payload);

		case SEARCH_VIDEO_MIND_ITEMS.SUCCESS: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPagesStartIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);
			const incomingPagesEndIndex: number = Math.floor(
				(requestPagination.offset + requestPagination.limit) / searchSettings.itemsPerPage
			);
			let updatedState = immutable(state);
			for (let i = incomingPagesStartIndex; i < incomingPagesEndIndex; i++) {
				const firstItemIndex = (i - incomingPagesStartIndex) * searchSettings.itemsPerPage;
				const lastItemIndex = (i - incomingPagesStartIndex + 1) * searchSettings.itemsPerPage;
				const pageData = action.result.data.slice(firstItemIndex, lastItemIndex);
				const newPage: MindItemPage = {
					status: AsyncActionStatus.READY,
					data: pageData.map((videoMindItem) => videoMindItem.id),
				};
				updatedState = updatedState.set(['videoMindItems', 'pages', i], newPage);
				updatedState = updatedState.merge(
					['videoMindItems', 'byId'],
					arrayToObject(pageData, (videoMindItem) => videoMindItem.id)
				);
			}
			return updatedState
				.set(['videoMindItems', 'pagination', 'total'], action.result.meta.pagination.total)
				.set(
					['videoMindItems', 'pagination', 'offset'],
					state.videoMindItems.pagination.offset + requestPagination.limit
				)
				.value();
		}

		case SEARCH_COURSE_MIND_ITEMS.START: {
			const requestPagination: Pagination = action.payload.pagination;
			const requestFilters: SearchFilters = action.payload.filters;
			const incomingPagesStartIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);
			const incomingPagesEndIndex: number = Math.floor(
				(requestPagination.offset + requestPagination.limit) / searchSettings.itemsPerPage
			);
			let updatedState = immutable(state);

			// biome-ignore lint/suspicious/noDoubleEquals: loose equality kept; strict equality changes null/undefined coercion semantics
			if (action.payload.pagination.offset == 0) {
				updatedState = updatedState.set(['courseMindItems'], createEmptySlice());
			}

			for (let i = incomingPagesStartIndex; i < incomingPagesEndIndex; i++) {
				updatedState = updatedState.set(['courseMindItems', 'pages', i], {
					status: AsyncActionStatus.LOADING,
					requestId: action.payload.requestId,
				});
			}

			updatedState = updatedState.set(['courseSearchFilters'], requestFilters);
			updatedState = updatedState.set(['courseResultsSearchId'], action.payload.searchId);
			searchResultsStorageManager.setCourseSearchFilters(requestFilters);
			return updatedState.value();
		}

		case SEARCH_COURSE_MIND_ITEMS.SUCCESS: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPagesStartIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);
			const incomingPagesEndIndex: number = Math.floor(
				(requestPagination.offset + requestPagination.limit) / searchSettings.itemsPerPage
			);
			let updatedState = immutable(state);
			for (let i = incomingPagesStartIndex; i < incomingPagesEndIndex; i++) {
				const firstItemIndex = (i - incomingPagesStartIndex) * searchSettings.itemsPerPage;
				const lastItemIndex = (i - incomingPagesStartIndex + 1) * searchSettings.itemsPerPage;
				const pageData = action.result.data.slice(firstItemIndex, lastItemIndex);
				const newPage: MindItemPage = {
					status: AsyncActionStatus.READY,
					data: pageData.map((courseMindItem) => courseMindItem.id),
				};
				updatedState = updatedState.set(['courseMindItems', 'pages', i], newPage);
				updatedState = updatedState.merge(
					['courseMindItems', 'byId'],
					arrayToObject(pageData, (courseMindItem) => courseMindItem.id)
				);
			}
			return updatedState
				.set(['courseMindItems', 'pagination', 'total'], action.result.meta.pagination.total)
				.set(
					['courseMindItems', 'pagination', 'offset'],
					state.courseMindItems.pagination.offset + requestPagination.limit
				)
				.value();
		}

		case SEARCH_PRODUCTS.START:
			return startLibrarySearch(state, 'products', action.payload);

		case SEARCH_PRODUCTS.SUCCESS: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPagesStartIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);
			const incomingPagesEndIndex: number = Math.floor(
				(requestPagination.offset + requestPagination.limit) / searchSettings.itemsPerPage
			);
			let updatedState = immutable(state);
			for (let i = incomingPagesStartIndex; i < incomingPagesEndIndex; i++) {
				const firstItemIndex = (i - incomingPagesStartIndex) * searchSettings.itemsPerPage;
				const lastItemIndex = (i - incomingPagesStartIndex + 1) * searchSettings.itemsPerPage;
				const pageData = action.result.data.slice(firstItemIndex, lastItemIndex);
				const newPage: ProductPage = {
					status: AsyncActionStatus.READY,
					data: pageData.map((product) => product.id),
				};
				updatedState = updatedState.set(['products', 'pages', i], newPage);
				updatedState = updatedState.merge(
					['products', 'byId'],
					arrayToObject(pageData, (product) => product.id)
				);
			}
			return updatedState
				.set(['products', 'pagination', 'total'], action.result.meta.pagination.total)
				.set(
					['products', 'pagination', 'offset'],
					state.products.pagination.offset + requestPagination.limit
				)
				.value();
		}

		case SEARCH_MIND_ITEMS.ERROR: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPageIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);

			return immutable(state)
				.set(['mindItems', 'pages', incomingPageIndex], {
					status: AsyncActionStatus.ERROR,
					error: action.error.message,
				})
				.value();
		}

		case SEARCH_VIDEO_MIND_ITEMS.ERROR: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPageIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);

			return immutable(state)
				.set(['videoMindItems', 'pages', incomingPageIndex], {
					status: AsyncActionStatus.ERROR,
					error: action.error.message,
				})
				.value();
		}

		case SEARCH_COURSE_MIND_ITEMS.ERROR: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPageIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);

			return immutable(state)
				.set(['courseMindItems', 'pages', incomingPageIndex], {
					status: AsyncActionStatus.ERROR,
					error: action.error.message,
				})
				.value();
		}

		case SEARCH_PRODUCTS.ERROR: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPageIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);

			return immutable(state)
				.set(['products', 'pages', incomingPageIndex], {
					status: AsyncActionStatus.ERROR,
					error: action.error.message,
				})
				.value();
		}

		case SEARCH_UNIFIED.START:
			return startLibrarySearch(state, 'unified', action.payload);

		case SEARCH_UNIFIED.SUCCESS: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPagesStartIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);
			const incomingPagesEndIndex: number = Math.floor(
				(requestPagination.offset + requestPagination.limit) / searchSettings.itemsPerPage
			);
			let updatedState = immutable(state);
			for (let i = incomingPagesStartIndex; i < incomingPagesEndIndex; i++) {
				const firstItemIndex = (i - incomingPagesStartIndex) * searchSettings.itemsPerPage;
				const lastItemIndex = (i - incomingPagesStartIndex + 1) * searchSettings.itemsPerPage;
				const pageData = action.result.data.slice(firstItemIndex, lastItemIndex);
				const newPage: UnifiedPage = {
					status: AsyncActionStatus.READY,
					data: pageData.map((item) => item.id),
				};
				updatedState = updatedState.set(['unified', 'pages', i], newPage);
				updatedState = updatedState.merge(
					['unified', 'byId'],
					arrayToObject(pageData, (item) => item.id)
				);
			}
			return updatedState
				.set(['unified', 'pagination', 'total'], action.result.meta.pagination.total)
				.set(
					['unified', 'pagination', 'offset'],
					state.unified.pagination.offset + requestPagination.limit
				)
				.value();
		}

		case SEARCH_UNIFIED.ERROR: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPageIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);

			return immutable(state)
				.set(['unified', 'pages', incomingPageIndex], {
					status: AsyncActionStatus.ERROR,
					error: action.error.message,
				})
				.value();
		}

		case SEARCH_REQUEST_ABORTED: {
			const { slice, requestId, pagination } = action.payload;
			const abortedPageIndex = Math.floor(pagination.offset / searchSettings.itemsPerPage);
			const abortedPage = state[slice].pages[abortedPageIndex];
			// A READY page there means the request settled before its abort arrived; a LOADING
			// page under another id belongs to the request that replaced this one.
			if (abortedPage?.status !== AsyncActionStatus.LOADING || abortedPage.requestId !== requestId) {
				return state;
			}

			const remainingPages = state[slice].pages.slice(0, abortedPageIndex);
			if (remainingPages.length === 0) {
				return immutable(state).set([slice], createEmptySlice()).value();
			}
			// The pagination offset only advances on SUCCESS, so load-more requests the dropped
			// page again.
			return immutable(state).set([slice, 'pages'], remainingPages).value();
		}

		case SEARCH_TOPICS.START: {
			return immutable(state).set(['subTopics', 'status'], AsyncActionStatus.LOADING).value();
		}

		case SEARCH_TOPICS.ERROR: {
			return immutable(state).set(['subTopics', 'status'], AsyncActionStatus.ERROR).value();
		}

		case SEARCH_TOPICS.SUCCESS: {
			return immutable(state)
				.set(['subTopics', 'terms', action.payload.subject], action.result)
				.set(['subTopics', 'status'], AsyncActionStatus.READY)
				.value();
		}

		case FETCH_WORKLOADS.START:
			return immutable(state).set(['workloads', 'status'], AsyncActionStatus.LOADING).value();
		case FETCH_WORKLOADS.SUCCESS:
			return immutable(state)
				.set(['workloads', 'data'], action.result)
				.set(['workloads', 'status'], AsyncActionStatus.READY)
				.value();
		case FETCH_WORKLOADS.ERROR:
			return immutable(state).set(['workloads', 'status'], AsyncActionStatus.ERROR).value();

		case FETCH_TEACHING_FORMATS.START:
			return immutable(state).set(['teachingFormats', 'status'], AsyncActionStatus.LOADING).value();
		case FETCH_TEACHING_FORMATS.SUCCESS:
			return immutable(state)
				.set(['teachingFormats', 'data'], action.result)
				.set(['teachingFormats', 'status'], AsyncActionStatus.READY)
				.value();
		case FETCH_TEACHING_FORMATS.ERROR:
			return immutable(state).set(['teachingFormats', 'status'], AsyncActionStatus.ERROR).value();

		case PIN_MIND_ITEM.START:
		case PIN_PRODUCT.START:
		case UNPIN_DOCUMENT.ERROR: {
			const itemId = action.payload.id;
			const type = action.payload.type;

			const subStores: SearchSlice[] = [];
			if (type === 'mindItem') {
				subStores.push(
					...getSubStores(state, itemId, ['videoMindItem', 'unified', 'mindItem', 'courseMindItem'])
				);
			} else if (type === 'product') {
				subStores.push(...getSubStores(state, itemId, ['unified', 'product']));
			}

			if (!subStores.length) {
				return state;
			}

			let updatedState = state;
			// biome-ignore lint/suspicious/useIterableCallbackReturn: pre-existing callback shape; changing returns needs per-site behavior review
			subStores.map((subStore) => {
				const byId = updatedState[subStore].byId;

				if (!byId[action.payload.id]) return;

				const newlyPinnedItem = { ...updatedState[subStore].byId[action.payload.id], isPinned: true };
				updatedState = immutable(updatedState)
					.merge([subStore, 'byId', action.payload.id], newlyPinnedItem)
					.value();
			});

			return updatedState;
		}

		case PIN_MIND_ITEM.ERROR:
		case PIN_PRODUCT.ERROR:
		case UNPIN_DOCUMENT.START: {
			const itemId = action.payload.id;
			const type = action.payload.type;

			const subStores: SearchSlice[] = [];
			if (type === 'mindItem') {
				subStores.push(
					...getSubStores(state, itemId, ['videoMindItem', 'unified', 'mindItem', 'courseMindItem'])
				);
			} else if (type === 'product') {
				subStores.push(...getSubStores(state, itemId, ['unified', 'product']));
			}

			if (!subStores.length) {
				return state;
			}

			let updatedState = state;
			// biome-ignore lint/suspicious/useIterableCallbackReturn: pre-existing callback shape; changing returns needs per-site behavior review
			subStores.map((subStore) => {
				const byId = updatedState[subStore].byId;
				if (!byId[action.payload.id]) return;

				const newlyPinnedItem = { ...updatedState[subStore].byId[action.payload.id], isPinned: false };

				updatedState = immutable(updatedState)
					.merge([subStore, 'byId', action.payload.id], newlyPinnedItem)
					.value();
			});

			return updatedState;
		}

		case CREATE_COURSE_REGISTRATION.SUCCESS: {
			const courseMindItem = state.courseMindItems.byId[action.payload.mindItemId];

			if (!courseMindItem) {
				return state;
			}

			const newlyEnrolledCourseMindItem = {
				course: { registrationStatus: COURSE_REGISTRATION_STATUS.PROGRESS },
			} as CourseMindItemSearchResult;

			return immutable(state)
				.merge(['courseMindItems', 'byId', action.payload.mindItemId], newlyEnrolledCourseMindItem)
				.value();
		}

		case UPDATE_COURSE_REGISTRATION.SUCCESS: {
			if (!state.courseMindItems.byId[action.result.mindItemId]) {
				return state;
			}

			const newlyUpdatedItem = {
				course: {
					registrationStatus:
						action.result.isCompleted && action.result.isPassed ? 'success' : 'progress',
				},
			} as Partial<MindItem<'course'>>;

			return immutable(state)
				.merge(['courseMindItems', 'byId', action.result.mindItemId], newlyUpdatedItem)
				.value();
		}

		default:
			return state;
	}
};

export default search;
